import {
  Alert,
  Button,
  Container,
  Stack,
  TextField,
  Typography,
  Paper,
  InputAdornment,
  IconButton,
  Divider,
} from "@mui/material";
import { Box } from "@mui/system";
import React, { useState } from "react";
import { signup } from "../../api/users";
import { loginUser } from "../../helpers/authHelper";
import { useNavigate, Link } from "react-router-dom";
import { isLength, isEmail, contains } from "validator";
import Copyright from "../Copyright";

import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

const SignupView = () => {
  const navigate = useNavigate();

  const [serverError, setServerError] = useState("");
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validate = () => {
    const errors = {};

    if (!isLength(formData.username, { min: 6, max: 30 })) {
      errors.username = "Username must be 6–30 characters";
    }

    if (contains(formData.username, " ")) {
      errors.username = "Username must not contain spaces";
    }

    if (!isEmail(formData.email)) {
      errors.email = "Enter a valid email address";
    }

    if (!isLength(formData.password, { min: 8 })) {
      errors.password = "Password must be at least 8 characters";
    }

    setErrors(errors);
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length) return;

    const data = await signup(formData);

    if (data.error) {
      setServerError(data.error);
    } else {
      loginUser(data);
      navigate("/");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(180deg, #F9FAFB 0%, #EEF2FF 100%)",
        px: 2,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 5 },
            borderRadius: 4,
            background: "rgba(255,255,255,0.9)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(0,0,0,0.05)",
            boxShadow: "0 20px 40px rgba(0,0,0,0.08)",
          }}
        >
          {/* Brand Header */}
          <Stack spacing={1.5} alignItems="center" mb={4}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "14px",
                background: "linear-gradient(135deg,#7C3AED,#9333EA)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 26,
                color: "#fff",
              }}
            >
              💬
            </Box>
  
            <Typography variant="h4" fontWeight={700}>
              Create an account
            </Typography>
  
            <Typography variant="body2" color="text.secondary" align="center">
              Start conversations, share ideas, and connect with people on ChatLog
            </Typography>
          </Stack>
  
          {/* Form */}
          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={2.2}>
              <TextField
                fullWidth
                label="Username"
                name="username"
                onChange={handleChange}
                error={!!errors.username}
                helperText={errors.username}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
  
              <TextField
                fullWidth
                label="Email address"
                name="email"
                onChange={handleChange}
                error={!!errors.email}
                helperText={errors.email}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
  
              <TextField
                fullWidth
                label="Password"
                type={showPassword ? "text" : "password"}
                name="password"
                onChange={handleChange}
                error={!!errors.password}
                helperText={errors.password}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
  
              {serverError && (
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                  {serverError}
                </Alert>
              )}
  
              <Button
                type="submit"
                size="large"
                variant="contained"
                sx={{
                  py: 1.5,
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: "none",
                  background:
                    "linear-gradient(135deg,#7C3AED 0%,#9333EA 100%)",
                  boxShadow: "0 8px 20px rgba(124,58,237,0.35)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg,#6D28D9 0%,#7E22CE 100%)",
                  },
                }}
              >
                Create account
              </Button>
            </Stack>
          </Box>
  
          <Divider sx={{ my: 4 }} />
  
          {/* Footer */}
          <Stack spacing={1.5} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Already have an account?{" "}
              <Link
                to="/login"
                style={{
                  color: "#7C3AED",
                  fontWeight: 600,
                  textDecoration: "none",
                }}
              >
                Sign in
              </Link>
            </Typography>
  
            <Copyright />
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
  
};  

export default SignupView;
